import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import { IconPlus, IconPencil } from '@tabler/icons-react';
import { useAuth } from '../../../features/auth';
import { MainSidebar, useSidebar } from '../../../widgets/main-sidebar';
import { Topbar } from '../../../widgets/topbar';
import { Button } from '../../../shared/ui/Button';
import { ConfirmModal } from '../../../shared/ui/ConfirmModal';
import {
  fetchCatalogs, createCatalog, updateCatalog, patchCatalogSort, deleteCatalog,
  type Catalog, type CatalogInput,
} from '../../../entities/catalog';
import { fetchGroups, fetchMyGroupIds, type Group } from '../../../entities/group';
import { RichTextEditor, TopicEditor, type TopicFormValues } from '../../../features/catalog-editor';
import { TopicTree } from './TopicTree';

type Mode = { kind: 'view' } | { kind: 'edit'; topic: Catalog } | { kind: 'create' };

const KnowledgeBasePage: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const { toggle } = useSidebar();
  const qc = useQueryClient();
  const isAdmin = currentUser?.role === 'ADMIN';
  const userId = currentUser?.id ?? '';

  const { data: topics = [], isLoading } = useQuery<Catalog[]>({
    queryKey: ['catalogs'],
    queryFn: fetchCatalogs,
  });
  const { data: groups = [] } = useQuery<Group[]>({
    queryKey: ['groups', 'all'],
    queryFn: fetchGroups,
    enabled: isAdmin, // список групп нужен только для редактора видимости
  });
  const { data: myGroupIds = [] } = useQuery<string[]>({
    queryKey: ['my-group-ids', userId],
    queryFn: () => fetchMyGroupIds(userId),
    enabled: !isAdmin && !!userId, // фильтр видимости нужен только обычным пользователям
  });

  const [selectedId, setSelectedId] = useState<string>('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<Mode>({ kind: 'view' });
  const [confirmDelete, setConfirmDelete] = useState<Catalog | null>(null);

  // Видимость по группам: обычный пользователь видит раздел, если у раздела нет групп
  // (пусто = виден всем) или его группы пересекаются с группами пользователя. Админ видит всё.
  const visibleTopics = useMemo(() => {
    if (isAdmin) return topics;
    const canSee = (s: Catalog) => !s.groups?.length || s.groups.some(g => myGroupIds.includes(g));
    const rootId = (t: Catalog) => {
      let cur = t;
      while (cur.texts.parentId) {
        const p = topics.find(x => x.id === cur.texts.parentId);
        if (!p) break;
        cur = p;
      }
      return cur.id;
    };
    const okRoots = new Set(topics.filter(t => !t.texts.parentId && canSee(t)).map(t => t.id));
    return topics.filter(t => okRoots.has(rootId(t)));
  }, [topics, isAdmin, myGroupIds]);

  // При открытии (однократно): первый видимый раздел выбран, ветки раскрыты.
  useEffect(() => {
    if (!visibleTopics.length || selectedId) return;
    setSelectedId(visibleTopics[0].id);
    setExpanded(new Set(visibleTopics.filter(t => visibleTopics.some(c => c.texts.parentId === t.id)).map(t => t.id)));
  }, [visibleTopics, selectedId]);

  const selected = topics.find(t => t.id === selectedId) ?? null;

  const descendantIds = (id: string): string[] => {
    const kids = topics.filter(t => t.texts.parentId === id);
    return kids.flatMap(k => [k.id, ...descendantIds(k.id)]);
  };

  const invalidate = () => qc.invalidateQueries({ queryKey: ['catalogs'] });
  const createMut = useMutation({ mutationFn: createCatalog, onSuccess: (rec) => { invalidate(); setSelectedId(rec.id); setMode({ kind: 'view' }); } });
  const updateMut = useMutation({ mutationFn: (v: { id: string; input: CatalogInput }) => updateCatalog(v.id, v.input), onSuccess: () => { invalidate(); setMode({ kind: 'view' }); } });
  const deleteMut = useMutation({
    mutationFn: async (topic: Catalog) => {
      // Каскад: узел + все вложенные (темы и подтемы) — иначе дети остаются «висеть» без родителя.
      const ids = [topic.id, ...descendantIds(topic.id)];
      await Promise.all(ids.map(deleteCatalog));
    },
    onSuccess: () => { invalidate(); setConfirmDelete(null); setSelectedId(''); setMode({ kind: 'view' }); },
  });
  const moveMut = useMutation({
    mutationFn: (p: { key: 'order' | 'subcategory'; patches: { id: string; pos: number }[] }) =>
      Promise.all(p.patches.map(x => patchCatalogSort(x.id, { [p.key]: x.pos }))),
    onSuccess: invalidate,
  });

  const onToggle = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const sectionsSorted = () => topics.filter(t => !t.texts.parentId).sort((a, b) => a.order - b.order);
  const childrenSorted = (pid: string) => topics.filter(t => t.texts.parentId === pid).sort((a, b) => a.subcategory - b.subcategory);

  const siblings = (parentId: string | null) =>
    parentId ? childrenSorted(parentId) : sectionsSorted();

  const nextPos = (parentId: string | null) => {
    const group = siblings(parentId);
    const key = parentId ? 'subcategory' : 'order';
    return group.reduce((m, t) => Math.max(m, t[key] ?? 0), 0) + 10;
  };

  // Родителем может быть раздел (ур.1) или тема (ур.2) → ребёнок станет темой или подтемой (макс. 3 уровня).
  // Исключаем сам узел и всех его потомков (нельзя вложить узел в собственного ребёнка).
  const parentOptions = (excludeId?: string) => {
    const excluded = new Set<string>(excludeId ? [excludeId, ...descendantIds(excludeId)] : []);
    const opts: { value: string; label: string }[] = [];
    sectionsSorted().forEach(sec => {
      if (!excluded.has(sec.id)) opts.push({ value: sec.id, label: sec.texts.title });
      childrenSorted(sec.id).forEach(topic => {
        if (!excluded.has(topic.id)) opts.push({ value: topic.id, label: '— ' + topic.texts.title });
      });
    });
    return opts;
  };

  const groupOptions = groups.map(g => ({ value: g.id, label: g.name ?? g.id }));

  const descCounts = (id: string) => {
    const kids = topics.filter(t => t.texts.parentId === id);
    const deeper = kids.reduce((n, k) => n + descendantIds(k.id).length, 0);
    return { kids: kids.length, deeper };
  };

  const handleSave = (values: TopicFormValues) => {
    const target = mode.kind === 'edit' ? mode.topic : null;
    const isRoot = !values.parentId;
    const stayed = target && (target.texts.parentId ?? null) === (values.parentId ?? null);
    const order = isRoot ? (stayed ? target!.order : nextPos(null)) : 0;
    const subcategory = isRoot ? 0 : (stayed ? target!.subcategory : nextPos(values.parentId));
    const input: CatalogInput = {
      title: values.title,
      parentId: values.parentId,
      html: values.html,
      order,
      subcategory,
      groups: isRoot ? (values.groups ?? []) : [], // видимость по группам — только у разделов
    };

    if (mode.kind === 'create') createMut.mutate(input);
    else if (target) updateMut.mutate({ id: target.id, input });
  };

  const handleMove = (topic: Catalog, dir: 'up' | 'down') => {
    const parentId = topic.texts.parentId ?? null;
    const key = parentId ? 'subcategory' : 'order';
    const group = siblings(parentId);
    const idx = group.findIndex(s => s.id === topic.id);
    const swap = dir === 'up' ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= group.length) return;

    const reordered = [...group];
    [reordered[idx], reordered[swap]] = [reordered[swap], reordered[idx]];

    // Переиндексируем всю группу (10,20,30…); пишем только изменившееся — так из общих нулей порядок встаёт корректно.
    const patches = reordered
      .map((t, i) => ({ id: t.id, pos: (i + 1) * 10 }))
      .filter(p => (group.find(g => g.id === p.id)![key] ?? 0) !== p.pos);
    if (patches.length) moveMut.mutate({ key, patches });
  };

  if (!currentUser) return null;

  const editing = mode.kind !== 'view';
  const editTopic = mode.kind === 'edit' ? mode.topic : null;

  const deleteCountsText = (t: Catalog) => {
    const { kids, deeper } = descCounts(t.id);
    const parts = [];
    if (kids) parts.push(`тем: ${kids}`);
    if (deeper) parts.push(`подтем: ${deeper}`);
    return parts.length
      ? `Внутри — ${parts.join(', ')}. Будет удалено всё вместе с разделом.`
      : 'Вложенных тем нет.';
  };

  return (
    <div id="app-screen">
      <Topbar user={currentUser} onLogout={logout} onSidebarToggle={toggle} />
      <div className="app-body">
        <MainSidebar isAdmin={isAdmin} isManager={currentUser.role === 'MANAGER'} />
        <div className="app-content kb-content">
          {isLoading ? (
            <div className="kb-empty">Загрузка…</div>
          ) : (
            <div className="kb-layout">
              <aside className="kb-sidebar">
                {isAdmin && (
                  <Button variant="adm-add" onClick={() => setMode({ kind: 'create' })} style={{ width: '100%', marginBottom: 8 }}>
                    <IconPlus size={16} /> Добавить раздел
                  </Button>
                )}
                {visibleTopics.length === 0 ? (
                  <div className="kb-empty">{isAdmin ? 'Создайте первый раздел.' : 'Разделы недоступны.'}</div>
                ) : (
                  <TopicTree
                    topics={visibleTopics}
                    selectedId={selectedId}
                    expanded={expanded}
                    isAdmin={isAdmin}
                    onSelect={(id) => { setSelectedId(id); setMode({ kind: 'view' }); }}
                    onToggle={onToggle}
                    onMove={handleMove}
                  />
                )}
              </aside>

              <article className="kb-article">
                {editing ? (
                  <TopicEditor
                    key={editTopic?.id ?? 'new'}
                    topic={editTopic}
                    parentOptions={parentOptions(editTopic?.id)}
                    groupOptions={groupOptions}
                    editor={(value, onChange) => <RichTextEditor value={value} onChange={onChange} />}
                    onSave={handleSave}
                    onCancel={() => setMode({ kind: 'view' })}
                    onDelete={editTopic ? () => setConfirmDelete(editTopic) : undefined}
                    saving={createMut.isPending || updateMut.isPending}
                  />
                ) : selected ? (
                  <>
                    <div className="kb-article-head">
                      <h1 className="kb-title">{selected.texts.title}</h1>
                      {isAdmin && (
                        <Button variant="adm-edit" onClick={() => setMode({ kind: 'edit', topic: selected })}>
                          <IconPencil size={16} /> Редактировать
                        </Button>
                      )}
                    </div>
                    <div className="kb-html" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selected.texts.html) }} />
                  </>
                ) : (
                  <div className="kb-empty">Выберите раздел слева.</div>
                )}
              </article>
            </div>
          )}
        </div>
      </div>

      {confirmDelete && (
        <ConfirmModal
          title="Удалить?"
          content={<>«{confirmDelete.texts.title}» будет удалено безвозвратно.<br />{deleteCountsText(confirmDelete)}</>}
          onConfirm={() => deleteMut.mutate(confirmDelete)}
          onClose={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
};

export { KnowledgeBasePage };
